/**
 * Advisory Orchestration Dashboard
 *
 * Dashboard for orchestrators to manage advisory requests
 * Features:
 * - View all advisory requests
 * - Filter by status
 * - Search requests
 * - View request details
 * - Create invitation links
 */

import React, { useState, useCallback } from 'react';

import { Box } from '@mui/material';

import CreateLinkModal from './components/CreateLinkModal';
import FiltersBar from './components/FiltersBar';
import RequestDetailsModal from './components/RequestDetailsModal';
import RequestsList from './components/RequestsList';
import { useAdvisoryRequests } from './hooks/useAdvisoryRequests';
import { useCreateInvitationLink } from './hooks/useCreateInvitationLink';
import * as S from './styles';
import Button from 'components/Button';
import Text from 'components/Text';
import { LeadRequestWithUserData, LeadRequestStatus } from 'types/leadRequestsTypes';

export default function LeadRequestOrchestration() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<LeadRequestWithUserData | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isCreateLinkModalOpen, setIsCreateLinkModalOpen] = useState(false);
  const [requestForLink, setRequestForLink] = useState<LeadRequestWithUserData | null>(null);

  // Fetch requests with filters
  const {
    requests,
    loading,
    error,
    refetch,
    statusCounts,
    page,
    setPage,
    size,
    total,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
  } = useAdvisoryRequests({
    searchTerm,
    status: selectedStatus,
  });

  // Create invitation link hook
  const { createLink, loading: creatingLink, linkData } = useCreateInvitationLink();

  const handleViewDetails = useCallback((request: LeadRequestWithUserData) => {
    setSelectedRequest(request);
    setIsDetailsModalOpen(true);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    console.log('handleCloseDetailsModal');
    setIsDetailsModalOpen(false);
    setSelectedRequest(null);
  }, []);

  const handleOpenCreateLinkModal = useCallback((request: LeadRequestWithUserData) => {
    setRequestForLink(request);
    setIsCreateLinkModalOpen(true);
  }, []);

  const handleCloseCreateLinkModal = useCallback(() => {
    setIsCreateLinkModalOpen(false);
    setRequestForLink(null);
  }, []);

  const handleCreateLink = useCallback(
    async (formData: {
      invitee_first_name: string;
      invitee_last_name: string;
      invitee_cell_phone: string;
      invitee_email: string;
      notify_user: boolean;
    }) => {
      if (!requestForLink) return;

      const link = await createLink(requestForLink.id, formData);

      if (link) {
        // Refresh the requests list to show the new link
        refetch();
      }
    },
    [requestForLink, createLink, refetch]
  );

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedStatus('');
  }, []);

  const handleStatusFilterClick = useCallback((status: LeadRequestStatus) => {
    setSelectedStatus(prev => (prev === status ? '' : status));
  }, []);

  return (
    <S.Container>
      {/* Header
      <S.Header>
        <S.Title>Gestão de solicitações de leads</S.Title>
        <Text size="medium" color="textSecondary">
          Gerencie solicitações de leads e crie links de convite
        </Text>
      </S.Header> */}

      {/* Stats Bar */}
      <S.StatsContainer>
        <S.StatCard active={selectedStatus === ''} onClick={() => setSelectedStatus('')}>
          <S.StatValue>{requests.length}</S.StatValue>
          <S.StatLabel>Total de solicitações</S.StatLabel>
        </S.StatCard>

        <S.StatCard
          active={selectedStatus === LeadRequestStatus.NEW}
          onClick={() => handleStatusFilterClick(LeadRequestStatus.NEW)}
        >
          <S.StatValue>{statusCounts[LeadRequestStatus.NEW]}</S.StatValue>
          <S.StatLabel>Novo</S.StatLabel>
        </S.StatCard>

        <S.StatCard
          active={selectedStatus === LeadRequestStatus.REQUESTED}
          onClick={() => handleStatusFilterClick(LeadRequestStatus.REQUESTED)}
        >
          <S.StatValue>{statusCounts[LeadRequestStatus.REQUESTED]}</S.StatValue>
          <S.StatLabel>Solicitado</S.StatLabel>
        </S.StatCard>

        <S.StatCard
          active={selectedStatus === LeadRequestStatus.ACCEPTED}
          onClick={() => handleStatusFilterClick(LeadRequestStatus.ACCEPTED)}
        >
          <S.StatValue>{statusCounts[LeadRequestStatus.ACCEPTED]}</S.StatValue>
          <S.StatLabel>Aceito</S.StatLabel>
        </S.StatCard>

        <S.StatCard
          active={selectedStatus === LeadRequestStatus.REJECTED}
          onClick={() => handleStatusFilterClick(LeadRequestStatus.REJECTED)}
        >
          <S.StatValue>{statusCounts[LeadRequestStatus.REJECTED]}</S.StatValue>
          <S.StatLabel>Rejeitado</S.StatLabel>
        </S.StatCard>
      </S.StatsContainer>

      {/* Filters */}
      <FiltersBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        onClearFilters={handleClearFilters}
        sortBy={sortBy}
        onSortChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Action Bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={2}>
        <Text size="medium" bold>
          {total} {total === 1 ? 'Solicitação' : 'Solicitações'} encontradas
        </Text>
        <Button variant="minimal" onClick={refetch} size="small">
          Atualizar
        </Button>
      </Box>

      {/* Requests List */}
      <RequestsList
        requests={requests}
        loading={loading}
        error={error}
        onViewDetails={handleViewDetails}
        onCreateLink={handleOpenCreateLinkModal}
        viewMode={viewMode}
        totalItems={total}
        currentPage={page}
        itemsPerPage={size}
        onPageChange={setPage}
      />

      {/* Request Details Modal */}
      <RequestDetailsModal
        request={selectedRequest}
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        onCreateLink={handleOpenCreateLinkModal}
      />

      {/* Create Link Modal */}
      <CreateLinkModal
        request={requestForLink}
        isOpen={isCreateLinkModalOpen}
        onClose={handleCloseCreateLinkModal}
        onLinkCreated={handleCreateLink}
        createdLink={linkData}
        loading={creatingLink}
      />
    </S.Container>
  );
}
